import React, { useState } from 'react';
import axios from 'axios';
import { Users, CheckCircle, Waves, Upload, FileText } from 'lucide-react';
import { toast, Toaster } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const RegistroCliente = () => {
  const [registroExitoso, setRegistroExitoso] = useState(false);
  const [enviando, setEnviando] = useState(false);
  const [archivoCedula, setArchivoCedula] = useState(null);
  const [formData, setFormData] = useState({
    nombre: '',
    apellido: '',
    rut: '',
    direccion: '',
    telefono: '',
    email: '',
    cedula_url: null,
    notas: ''
  });

  const subirArchivoCedula = async () => {
    if (!archivoCedula) return null;
    
    try {
      const formDataUpload = new FormData();
      formDataUpload.append('file', archivoCedula);
      
      const response = await axios.post(`${API}/upload-cedula-publico`, formDataUpload, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });
      
      return response.data.url;
    } catch (error) {
      console.error('Error al subir cédula:', error);
      toast.error('Error al subir archivo de cédula');
      return null;
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validación de campos requeridos
    if (!formData.nombre.trim()) {
      toast.error('El nombre es requerido');
      return;
    }
    if (!formData.apellido.trim()) {
      toast.error('El apellido es requerido');
      return;
    }
    if (!formData.rut.trim()) {
      toast.error('El RUT/Documento es requerido');
      return;
    }
    if (!formData.direccion.trim()) {
      toast.error('La dirección es requerida');
      return;
    }
    if (!formData.telefono.trim()) {
      toast.error('El teléfono es requerido');
      return;
    }
    if (!formData.email.trim()) {
      toast.error('El email es requerido');
      return;
    }
    if (!archivoCedula) {
      toast.error('La cédula o documento de identidad es requerido');
      return;
    }

    setEnviando(true);

    try {
      // Subir cédula (ahora es obligatoria)
      const cedulaUrl = await subirArchivoCedula();
      if (!cedulaUrl) {
        setEnviando(false);
        return; // Si falló la subida, no continuar
      }

      // Registrar cliente
      await axios.post(`${API}/registro-cliente-publico`, {
        ...formData,
        cedula_url: cedulaUrl
      });

      setRegistroExitoso(true);
      toast.success('¡Registro exitoso!');
    } catch (error) {
      console.error('Error al registrar:', error);
      toast.error('Error al registrar. Por favor intenta nuevamente.');
    } finally {
      setEnviando(false);
    }
  };

  const handleReset = () => {
    setFormData({
      nombre: '',
      apellido: '',
      rut: '',
      direccion: '',
      telefono: '',
      email: '',
      cedula_url: null,
      notas: ''
    });
    setArchivoCedula(null);
    setRegistroExitoso(false);
  };

  if (registroExitoso) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-cyan-50 via-sky-50 to-blue-50 flex items-center justify-center p-4">
        <Toaster position="top-right" />
        <div className="max-w-md w-full bg-white rounded-2xl shadow-2xl p-8 text-center">
          <div className="mb-6">
            <div className="w-20 h-20 bg-gradient-to-br from-green-400 to-green-600 rounded-full mx-auto flex items-center justify-center mb-4">
              <CheckCircle className="w-12 h-12 text-white" />
            </div>
            <h2 className="text-3xl font-bold text-cyan-800 mb-2">
              ¡Registro Exitoso!
            </h2>
            <p className="text-gray-600">
              Gracias por registrarte. Nos pondremos en contacto contigo pronto.
            </p>
          </div>
          
          <div className="flex gap-3 justify-center">
            <Button
              onClick={handleReset}
              className="bg-gradient-to-r from-cyan-600 to-cyan-700 hover:from-cyan-700 hover:to-cyan-800"
            >
              Registrar otro cliente
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-cyan-50 via-sky-50 to-blue-50 flex items-center justify-center p-4">
      <Toaster position="top-right" />
      
      <div className="max-w-2xl w-full">
        {/* Header con branding */}
        <div className="text-center mb-8">
          <div className="flex items-center justify-center gap-3 mb-4">
            <Waves className="w-12 h-12 text-cyan-600" />
            <h1 className="text-4xl font-bold text-cyan-800" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
              R3NTapp
            </h1>
          </div>
          <p className="text-lg text-cyan-700">
            Registro de Cliente
          </p>
        </div>

        {/* Formulario */}
        <div className="bg-white rounded-2xl shadow-2xl p-8">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-12 h-12 bg-gradient-to-br from-cyan-600 to-cyan-700 rounded-full flex items-center justify-center">
              <Users className="w-6 h-6 text-white" />
            </div>
            <div>
              <h2 className="text-2xl font-bold text-cyan-800">
                Completa tus datos
              </h2>
              <p className="text-sm text-gray-600">
                Todos los campos son importantes para tu reserva
              </p>
            </div>
          </div>

          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Nombre y Apellido */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="nombre" className="text-cyan-800 font-semibold">
                  Nombre *
                </Label>
                <Input
                  id="nombre"
                  value={formData.nombre}
                  onChange={(e) => setFormData({ ...formData, nombre: e.target.value })}
                  placeholder="Juan"
                  required
                  className="mt-1"
                />
              </div>
              <div>
                <Label htmlFor="apellido" className="text-cyan-800 font-semibold">
                  Apellido *
                </Label>
                <Input
                  id="apellido"
                  value={formData.apellido}
                  onChange={(e) => setFormData({ ...formData, apellido: e.target.value })}
                  placeholder="Pérez"
                  required
                  className="mt-1"
                />
              </div>
            </div>

            {/* RUT y Dirección */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="rut" className="text-cyan-800 font-semibold">
                  RUT / Documento *
                </Label>
                <Input
                  id="rut"
                  value={formData.rut}
                  onChange={(e) => setFormData({ ...formData, rut: e.target.value })}
                  placeholder="12.345.678-9"
                  required
                  className="mt-1"
                />
              </div>
              <div>
                <Label htmlFor="direccion" className="text-cyan-800 font-semibold">
                  Dirección *
                </Label>
                <Input
                  id="direccion"
                  value={formData.direccion}
                  onChange={(e) => setFormData({ ...formData, direccion: e.target.value })}
                  placeholder="Calle Principal 123"
                  required
                  className="mt-1"
                />
              </div>
            </div>

            {/* Teléfono y Email */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="telefono" className="text-cyan-800 font-semibold">
                  Teléfono *
                </Label>
                <Input
                  id="telefono"
                  type="tel"
                  value={formData.telefono}
                  onChange={(e) => setFormData({ ...formData, telefono: e.target.value })}
                  placeholder="+56 9 1234 5678"
                  required
                  className="mt-1"
                />
              </div>
              <div>
                <Label htmlFor="email" className="text-cyan-800 font-semibold">
                  Email *
                </Label>
                <Input
                  id="email"
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  placeholder="correo@ejemplo.com"
                  required
                  className="mt-1"
                />
              </div>
            </div>

            {/* Cédula */}
            <div>
              <Label htmlFor="cedula" className="text-cyan-800 font-semibold">
                Cédula o Documento de Identidad *
              </Label>
              <div className="mt-1 flex items-center gap-3">
                <div className="flex-1">
                  <Input
                    id="cedula"
                    type="file"
                    accept="image/*,.pdf"
                    onChange={(e) => setArchivoCedula(e.target.files[0])}
                    required
                    className="file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-cyan-50 file:text-cyan-700 hover:file:bg-cyan-100"
                  />
                </div>
                {archivoCedula && (
                  <div className="flex items-center gap-2 text-sm text-green-600">
                    <FileText className="w-4 h-4" />
                    <span>{archivoCedula.name}</span>
                  </div>
                )}
              </div>
              <p className="text-xs text-gray-500 mt-1">
                Puedes subir una foto o PDF de tu documento
              </p>
            </div>

            {/* Notas */}
            <div>
              <Label htmlFor="notas" className="text-cyan-800 font-semibold">
                Comentarios adicionales (opcional)
              </Label>
              <textarea
                id="notas"
                value={formData.notas}
                onChange={(e) => setFormData({ ...formData, notas: e.target.value })}
                placeholder="Cualquier información adicional que quieras compartir..."
                rows={3}
                className="mt-1 w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-cyan-500"
              />
            </div>

            {/* Botón Submit */}
            <div className="pt-4">
              <Button
                type="submit"
                disabled={enviando}
                className="w-full py-6 text-lg bg-gradient-to-r from-cyan-600 to-cyan-700 hover:from-cyan-700 hover:to-cyan-800 shadow-lg hover:shadow-xl transition-all"
              >
                {enviando ? (
                  <span className="flex items-center justify-center gap-2">
                    <Upload className="w-5 h-5 animate-spin" />
                    Enviando...
                  </span>
                ) : (
                  <span className="flex items-center justify-center gap-2">
                    <CheckCircle className="w-5 h-5" />
                    Completar Registro
                  </span>
                )}
              </Button>
            </div>

            <p className="text-xs text-center text-gray-500 mt-4">
              * Todos los campos son requeridos excepto comentarios
            </p>
          </form>
        </div>

        {/* Footer */}
        <div className="text-center mt-6 text-gray-600">
          <p className="text-sm flex items-center justify-center gap-2">
            <Waves className="w-4 h-4 text-cyan-600" />
            R3NTapp - Gestión de Arriendos
          </p>
        </div>
      </div>
    </div>
  );
};

export default RegistroCliente;
